import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import PackageCard from './PackageCard';
import destinationsData from '../data/destinations.json';
import './SearchResults.css';

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Extract search parameters
  const query = searchParams.get('q') || 'Best Packages';
  const duration = searchParams.get('duration') || '';
  const minPrice = parseInt(searchParams.get('minPrice')) || 20000;
  const maxPrice = parseInt(searchParams.get('maxPrice')) || 500000;
  const includeFlights = searchParams.get('includeFlights') === 'true';

  useEffect(() => {
    setLoading(true);
    
    // Get all packages from destinations.json
    const allPackages = [];
    Object.values(destinationsData.destinations).forEach(destination => {
      destination.packages.forEach(pkg => {
        // Convert JSON package format to SearchResults format
        const convertedPkg = {
          id: pkg.id,
          title: pkg.title,
          destinations: pkg.itinerary,
          duration: pkg.duration,
          rating: parseFloat(pkg.rating.split(' ')[0]),
          reviewCount: pkg.rating.split('(')[1].replace(')', ''),
          originalPrice: parseInt(pkg.originalPrice.replace(/[^\d]/g, '')),
          discount: parseInt(pkg.savings.replace(/[^\d]/g, '')),
          finalPrice: parseInt(pkg.finalPrice.replace(/[^\d]/g, '')),
          images: pkg.images
        };
        allPackages.push(convertedPkg);
      });
    });

    let filteredPackages = allPackages;

    // Filter by destination (search term)
    if (query && query !== 'Best Packages') {
      const destinationKey = query.charAt(0).toUpperCase() + query.slice(1).toLowerCase();
      
      // First try exact destination match
      if (destinationsData.destinations[destinationKey]) {
        filteredPackages = filteredPackages.filter(pkg => 
          destinationsData.destinations[destinationKey].packages.some(dpkg => dpkg.id === pkg.id)
        );
      } else {
        // Fallback to text search in title/itinerary
        filteredPackages = filteredPackages.filter(pkg => 
          pkg.title.toLowerCase().includes(query.toLowerCase()) ||
          pkg.destinations.toLowerCase().includes(query.toLowerCase())
        );
      }
    }

    // Filter by price range
    filteredPackages = filteredPackages.filter(pkg => 
      pkg.finalPrice >= minPrice && pkg.finalPrice <= maxPrice
    );

    // Filter by duration if specified
    if (duration) {
      const filterByDuration = (pkgDuration, filterDuration) => {
        const pkgDays = parseInt(pkgDuration.match(/(\d+)/)[1]);
        
        switch (filterDuration) {
          case 'Upto 1 Day':
            return pkgDays <= 1;
          case '2 to 3 days':
            return pkgDays >= 2 && pkgDays <= 3;
          case '3 to 5 days':
            return pkgDays >= 3 && pkgDays <= 5;
          case '5 to 7 days':
            return pkgDays >= 5 && pkgDays <= 7;
          case '7+ Days':
            return pkgDays >= 7;
          default:
            return true;
        }
      };

      filteredPackages = filteredPackages.filter(pkg => 
        filterByDuration(pkg.duration, duration)
      );
    }

    // Filter by flights inclusion (title contains "Flights")
    if (includeFlights) {
      filteredPackages = filteredPackages.filter(pkg => 
        pkg.title.toLowerCase().includes('flights')
      );
    }

    // Simulate API delay
    setTimeout(() => {
      setPackages(filteredPackages);
      setLoading(false);
    }, 500);
  }, [searchParams]);

  return (
    <div className="search-results-page">
      <div className="container">
        {/* Header */}
        <div className="results-header">
          <h1 className="results-title">{query}</h1>
          <div className="results-subtitle">
            {duration && <span className="filter-tag">{duration}</span>}
            {includeFlights && <span className="filter-tag">Flights Included</span>}
            <span className="price-range">
              ₹{minPrice.toLocaleString()} - ₹{maxPrice.toLocaleString()}
            </span>
          </div>
        </div>

        {/* Results Count */}
        <div className="results-count">
          {loading ? (
            <span>Searching packages...</span>
          ) : (
            <span>{packages.length} packages found</span>
          )}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Finding the best packages for you...</p>
          </div>
        )}

        {/* No Results */}
        {!loading && packages.length === 0 && (
          <div className="no-results">
            <h3>No packages found</h3>
            <p>Try adjusting your search criteria</p>
          </div>
        )}

        {/* Package Grid */}
        {!loading && packages.length > 0 && (
          <div className="packages-grid">
            {packages.map((pkg) => (
              <PackageCard
                key={pkg.id}
                id={pkg.id}
                title={pkg.title}
                destinations={pkg.destinations}
                duration={pkg.duration}
                rating={pkg.rating}
                reviewCount={pkg.reviewCount}
                originalPrice={pkg.originalPrice}
                discount={pkg.discount}
                finalPrice={pkg.finalPrice}
                images={pkg.images}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchResults;
